import { createContext, useContext, useEffect, useId, useRef, type ReactNode } from 'react';
import type { DialogProps } from './Dialog';
import { Button, IconButton } from './primitives';
import { Plain } from '@/app/technical';
import { Stepper } from './status';
const Context = createContext<boolean | undefined>(undefined);
export function InlineForms({ active, children }: { active: boolean; children: ReactNode }) { return <Context.Provider value={active}>{children}</Context.Provider>; }
export function useInlineFormScope() { return useContext(Context); }

/** The original business form stays in its record tab, including while hidden.
 * Explicit decisions and callbacks are unchanged; no portal can expose another
 * tab's form or retain a global pointer/focus lock after a record switch. */
export function InlineDialog({ active, open, onOpenChange, title, kicker, danger, children, primary, secondary, note, step, onCloseAutoFocus }: DialogProps & { active: boolean }) {
  const titleId = useId(), descriptionId = useId(), panel = useRef<HTMLElement>(null);
  const restore = useRef<HTMLElement | null>(null), lastFocus = useRef<HTMLElement | null>(null);
  const wasOpen = useRef(false);
  const closeFocus = useRef(onCloseAutoFocus); closeFocus.current = onCloseAutoFocus;
  function returnFocus() {
      const event = new Event('closeAutoFocus', { cancelable: true }); closeFocus.current?.(event);
      if (!event.defaultPrevented && restore.current?.isConnected && !restore.current.closest('[hidden]')) restore.current.focus();
  }
  useEffect(() => () => { if (wasOpen.current) returnFocus(); }, []);
  useEffect(() => {
    if (open && !wasOpen.current) restore.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    if (!open && wasOpen.current) returnFocus();
    wasOpen.current = open;
    if (!open || !active) return;
    const target = lastFocus.current ?? panel.current?.querySelector<HTMLElement>('input, textarea, select, [contenteditable="true"]') ?? panel.current;
    target?.focus();
  }, [open, active]);
  if (!open) return null;
  return <section ref={panel} role="dialog" aria-modal={false} aria-labelledby={titleId} aria-describedby={descriptionId} tabIndex={-1} className={`my-4 border bg-page p-4 space-y-4 outline-none ${danger ? 'border-danger' : 'border-strong'}`}
    onFocusCapture={event => { if (event.target instanceof HTMLElement && event.target !== panel.current) lastFocus.current = event.target; }}
    onKeyDown={event => { if (active && event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); onOpenChange(false); } }}>
    <header className="space-y-2">
      {step && <Stepper at={step.at} of={step.of} />}
      <div className="flex items-start justify-between gap-3"><div><p id={descriptionId} className="text-label text-meta"><Plain>{kicker}</Plain></p><h2 id={titleId} className="text-panel font-semibold"><Plain>{title}</Plain></h2></div><IconButton label="Close" glyph="close" onClick={() => onOpenChange(false)} /></div>
    </header>
    <div className="space-y-4">{children}</div>
    <footer className="flex flex-wrap items-center gap-3 border-t border-line pt-3">
      {primary && <Button variant={danger ? 'danger-solid' : 'primary'} disabled={primary.disabled} onClick={primary.onClick}>{primary.label}</Button>}
      <Button variant="quiet" onClick={secondary.onClick}>{secondary.label}</Button>
      {note && <span className="text-label text-meta"><Plain>{note}</Plain></span>}
    </footer>
  </section>;
}
